import BaseChart from './BaseChart'
import ItemsHelper from '../chart_helpers/ItemsHelper'
import RootHelper from '../chart_helpers/RootHelper'
import SelectableHelper from '../chart_helpers/SelectableHelper'

import { interpolate as d3_interpolate } from 'd3-interpolate'
// import { arc as d3_arc } from 'd3-shape'

import { treemap as d3_treemap, hierarchy as d3_hierarchy } from 'd3-hierarchy'
import { select as d3_select } from 'd3-selection'
import { rgb as d3_rgb } from 'd3-color'
import SizeHelper from '../chart_helpers/SizeHelper'
import { ellipseSvgText } from '../utils/textUtils'
import { formatNumber } from '../utils/formatUtils'
import { getBrightness } from '../utils/colorUtils'
export default class TreemapChart extends BaseChart {
  constructor(...args) {
    super(...args)
  }
  static defaultConfig = {
    mode: {
      type: String,
      default: '',
      exposed: false,
    },
    dataSorting: {
      type: [String],
      default: 'none',
      options: ['none', 'asc', 'desc'],
      exposed: true,
    },
    labelValue: {
      type: [String],
      default: 'none',
      options: ['none', 'abs', 'percent'],
      exposed: true,
    },
    /**
     * If set, all values smaller than this threshold will be grouped in a single sector labeled "Others".
     */
    groupOthersThreshold: {
      type: Number,
      default: 0,
      exposed: true,
    },
    /**
     * If set to `true`, sector values will be show as a percentage of the total.
     */
    showPercentage: {
      type: Boolean,
      default: true,
      exposed: true,
    },
    /**
     * Format for the percentages.
     */
    percentageFormat: {
      type: [String],
      default: '[N]0,0.0',
      exposed: true,
    },
    /**
     * If a value is set, it will be displayed in the center of the donut. If set to `true`, the total sum of the sector values will be displayed.
     */
    centerValue: {
      type: [Boolean, Number, String, Object],
      default: true,
      exposed: true,
    },
    /**
     * If a value is set, it will be displayed in the above the central value of the donut.
     */
    centerTopText: {
      type: [String],
      // default: '<div><b>BOLD</b>&<i>ITALIC</i></div>',
      default: '',
      exposed: true,
    },
    /**
     * If a value is set, it will be displayed in the under the central value of the donut.
     */
    centerBottomText: {
      type: [String],
      // default: `<a href="https://www.google.es" target="_blank">link 1</a><span style="color: #f0f; text-decoration: underline; cursor: pointer" onclick="(function(){
      //   alert('Test Alert!');
      // })();">js function</span>`,
      default: '',
      exposed: true,
    },
    /**
     * If set, all sectors will span at lease this angle. Usefull if some values are too small to be seen.
     */
    minSectorAngle: {
      type: Number,
      default: 0,
      exposed: true,
    },
    /**
     * Thickness of the donut
     */
    donutThickness: {
      type: String,
      default: 'medium',
      options: ['thick', 'medium', 'thin'],
      exposed: true,
    },
    /**
     * Show/hide labels for each sector.
     */
    showLabels: {
      type: Boolean,
      default: true,
      exposed: true,
    },
    TooltipBehavior: {
      getHtml: function (d) {
        let formatter = (value, format) => {
          let match = this.matchFormatString(format || '')
          let numberFormat = match && match[2]
          let thouDel = this.config.thousandsDelimiter
          let decDel = this.config.decimalDelimiter
          return formatNumber(value, numberFormat, thouDel, decDel)
        }
        if (d.data.othersData) {
          let datas = d.data.othersData
          let html = datas.map(dd => {
            return `
            <div class="tip-value important">${this.valueWithUnit(dd.value)}</div>
            <div class="tip-id">
              <span class="icon" style="display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: ${this.color(
                { id: dd.id }
              )}; margin-right: 3px"></span>
              ${dd.id}
            </div>
            ${
              this.config.showPercentage
                ? '<div class="secondary">' +
                  formatter(dd.percent, this.config.percentageFormat) +
                  '%</div>'
                : ''
            }
            `
          })
          return html.join('<br/>')
        } else {
          return `
          <div class="tip-value important">${this.valueWithUnit(d.data.value)}</div>
          <div class="tip-id">
            <span class="icon" style="display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: ${this.color(
              { id: d.id }
            )}; margin-right: 3px"></span>
            ${d.id}
          </div>
          ${
            this.config.showPercentage
              ? '<div class="secondary">' +
                formatter(d.data.percent, this.config.percentageFormat) +
                '%</div>'
              : ''
          }
          `
        }
      },
      getItemOffset: function ($el, d) {
        let targetRect = $el.getBoundingClientRect()
        return {
          x: targetRect.x + targetRect.width / 2,
          y: targetRect.y + targetRect.height / 2,
        }
      },
    },
  }

  static helpers = [RootHelper, ItemsHelper, SelectableHelper, SizeHelper]
  formatter(value, format) {
    let match = this.matchFormatString(format || '')
    let numberFormat = match && match[2]
    let thouDel = this.config.thousandsDelimiter
    let decDel = this.config.decimalDelimiter
    return formatNumber(value, numberFormat, thouDel, decDel)
  }
  beforeHelpers() {
    // this.content = this.root.selectAll('.content').data([null])
    // this.content = this.content.merge(this.content.enter().append('g').attr('class','content'))
  }

  fakeColor(d) {
    if (this.config.ItemsHelper.colorScale === 'categorical') {
      return this.color(d.parentId ? { id: d.parentId } : d)
    } else {
      return this.color({ id: d.value })
    }
  }
  draw() {
    // Then d3.treemap computes the position of each element of the hierarchy
    let tree = this.data[0]
    d3_treemap().size([this.size.width, this.size.height]).padding(2).paddingInner(2)(tree)

    this.itemsEnter
      .filter(d => !d.data.children || d.data.children?.length === 0)
      .append('rect')
      .attr('x', d => d.x0)
      .attr('y', d => d.y0)
      .attr('width', d => d.x1 - d.x0)
      .attr('height', d => d.y1 - d.y0)
      .style('stroke', 'none')
      .style('opacity', 0)
      .style('display', d => (d.depth === 0 ? 'none' : 'inherit'))

    this.itemsEnter
      .filter(d => !d.data.children || d.data.children?.length === 0)
      .append('g')
      .attr('class', 'text-group')
      .style('opacity', 0)
      .attr('transform', d => `translate(${d.x0 + 10},${d.y0 + 10})`)
      .append('text')
      .attr('dominant-baseline', 'middle')
      .attr('fill', d => {
        let brightness = getBrightness(this.fakeColor(d))
        return brightness < 140 ? '#fff' : '#000'
      })
      .attr('text-anchor', 'start')

    this.items
      .merge(this.itemsEnter)
      .select('.text-group')
      .select('text')
      .html(
        d =>
          (d.data.parentLabel
            ? `<tspan font-weight="600" x="0" dy=".6em">${d.data.parentLabel}</tspan>`
            : '') +
          `<tspan font-size="14" font-weight="${d.data.parentLabel ? 400 : 600}" x="0" dy="${
            d.data.parentLabel ? '1.4em' : '.6em'
          }">${d.data.label}</tspan>
          <tspan x="0" dy="1.2em">${this.valueWithUnit(d.data.value)}</tspan>`
      )
      .each(function (d) {
        if (d.x1 - d.x0 > 80 && d.y1 - d.y0 > 80) {
          d3_select(this)
            .selectAll('tspan')
            .style('display', 'inherit')
            .call(ellipseSvgText, d.x1 - d.x0 - 20)
        } else {
          d3_select(this).selectAll('tspan').style('display', 'none')
        }
      })

    this.itemsEnterTrans
      .select('rect')
      .style('opacity', 1)
      .attr('fill', d => this.fakeColor(d))

    this.itemsEnterTrans.select('.text-group').style('opacity', 1)

    this.itemsTrans
      .select('rect')
      .attr('x', d => d.x0)
      .attr('y', d => d.y0)
      .attr('width', d => d.x1 - d.x0)
      .attr('height', d => d.y1 - d.y0)
      .attr('fill', d => this.fakeColor(d))
      .style('opacity', 1)

    this.itemsTrans
      .select('.text-group')
      .style('opacity', 1)
      .attr('transform', d => `translate(${d.x0 + 10},${d.y0 + 10})`)
      .select('text')
      .attr('fill', d => {
        let brightness = getBrightness(this.fakeColor(d))
        return brightness < 140 ? '#fff' : '#000'
      })

    this.itemsExitTrans.style('opacity', 0)

    // we must reset selection
    this.selection = this.data.filter(d => this.selection.find(dd => dd.id === d.id) !== undefined)
    this._updateSelection()
  }
  setSelectedFunc(sel, d) {
    if (this.config.mode !== 'donut') return
    let { radius, innerRadius } = this.getRadiuses()

    // let offset = (radius - innerRadius)*0.75
    let offset
    if (this.config.donutThickness === 'thin') offset = (radius - innerRadius) * 0.75
    else if (this.config.donutThickness === 'medium') offset = (radius - innerRadius) * 0.15
    else offset = (radius - innerRadius) * 0.1

    radius = radius + offset
    innerRadius = innerRadius - offset
    let arc = d3_arc().innerRadius(innerRadius).outerRadius(radius)
    // let glow = sel.selectAll('.glow').data(this.config.donutThickness==='thin'?[d]:[])
    let glow = sel.selectAll('.glow').data([d])
    glow.exit().remove()
    glow = glow.merge(glow.enter().append('path').attr('class', 'glow'))
    glow
      .attr('d', arc)
      .attr('fill', this.color(d))
      .attr('opacity', 0)
      .transition()
      .duration(200)
      .attr('opacity', 0.25)
  }
  unsetSelectedFunc(sel) {
    if (this.config.mode !== 'donut') return
    let glow = sel.selectAll('.glow').data([])
    glow.exit().remove()
  }
}
